/**
 * Profile Completion Utilities
 *
 * Utilities for checking if a user has completed required profile fields.
 * Required fields are needed for proper speaker token replacement in LLM-generated content.
 *
 * Required fields on user model:
 * - first_name: User's first name
 * - pronouns: User's pronouns (e.g., "he/him", "she/her", "they/them")
 *
 * Note: Partner information is managed via conversation partners (usePrimaryPartner hook),
 * not on the user model. The ProfileRequiredRoute component checks for both.
 */

import type { User } from "../types/user";

/**
 * Fields required for profile completion.
 * These fields are needed for speaker token replacement.
 *
 * Note: partner_name and partner_pronouns are managed via conversation partners
 * (see usePrimaryPartner hook), not on the user model directly.
 */
export const REQUIRED_PROFILE_FIELDS = ["first_name", "pronouns"] as const;

export type RequiredProfileField = (typeof REQUIRED_PROFILE_FIELDS)[number];

/**
 * Result of checking profile completion.
 */
export interface ProfileCompletionResult {
  /** Whether the profile is complete */
  isComplete: boolean;
  /** List of missing required fields */
  missingFields: RequiredProfileField[];
}

/**
 * Check if a user's profile is complete with all required fields.
 *
 * @param user - The user object to check, or null/undefined if not loaded
 * @returns Profile completion result with isComplete flag and missing fields
 */
export function checkProfileCompletion(
  user: User | null | undefined
): ProfileCompletionResult {
  if (!user) {
    return {
      isComplete: false,
      missingFields: [...REQUIRED_PROFILE_FIELDS],
    };
  }

  const missingFields: RequiredProfileField[] = [];

  for (const field of REQUIRED_PROFILE_FIELDS) {
    const value = user[field];
    // Check for null, undefined, or empty/whitespace-only strings
    if (value === null || value === undefined || value.trim() === "") {
      missingFields.push(field);
    }
  }

  return {
    isComplete: missingFields.length === 0,
    missingFields,
  };
}

/**
 * Get a human-readable label for a profile field.
 *
 * @param field - The field name
 * @returns Human-readable label
 */
export function getFieldLabel(field: RequiredProfileField): string {
  const labels: Record<RequiredProfileField, string> = {
    first_name: "Your First Name",
    pronouns: "Your Pronouns",
  };
  return labels[field];
}

/**
 * Format missing fields as a readable list.
 *
 * @param missingFields - Array of missing field names
 * @returns Formatted string listing missing fields
 */
export function formatMissingFields(
  missingFields: RequiredProfileField[]
): string {
  if (missingFields.length === 0) {
    return "";
  }

  const labels = missingFields.map(getFieldLabel);

  if (labels.length === 1) {
    return labels[0];
  }

  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  const lastLabel = labels.pop();
  return `${labels.join(", ")}, and ${lastLabel}`;
}
